/**
 * PeriodPicker — the universal date-range filter for Bills / Vendors /
 * Dashboard / Reports. Supports:
 *   • This Month · Last Month
 *   • This Quarter · Last Quarter  (Indian FY calendar quarters, see below)
 *   • This FY · Last FY  (Apr 1 – Mar 31, Indian Financial Year)
 *   • Custom range  (two <input type=date> pickers)
 *   • All time
 *
 * Emits a canonical `{ preset, from, to, label }` object. Consumers pass
 * `from` and `to` (both `YYYY-MM-DD`) to the backend as `date_from` /
 * `date_to` query params. When `preset === 'all'` both are null.
 *
 * State is persisted in localStorage under STORAGE_KEY so switching pages
 * remembers the choice (mirroring the old MonthPicker UX).
 *
 * Indian FY quarters (used by GST returns):
 *   Q1 = Apr–Jun, Q2 = Jul–Sep, Q3 = Oct–Dec, Q4 = Jan–Mar
 * "This quarter" is derived from today's date using that mapping.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';

const STORAGE_KEY = 'billlens.selectedPeriod';

// ---------- Date helpers (all in local time, YYYY-MM-DD strings) ---------- //

function toISODate(d) {
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function fmtHuman(d) {
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d)   { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }

// Indian FY starts Apr 1. `fyStartYear(today)` returns the calendar year the
// current FY started in. e.g. Jul 2026 → 2026 (FY 2026-27).
function fyStartYear(d) {
    return d.getMonth() >= 3 /* Apr */ ? d.getFullYear() : d.getFullYear() - 1;
}
function fyLabel(startYear) {
    const short = String((startYear + 1) % 100).padStart(2, '0');
    return `FY ${startYear}-${short}`;
}

// Indian FY quarter (1..4) from a Date. Q1 = Apr–Jun, Q4 = Jan–Mar.
function fyQuarter(d) {
    const m = d.getMonth(); // 0..11
    if (m >= 3 && m <= 5) return 1;
    if (m >= 6 && m <= 8) return 2;
    if (m >= 9 && m <= 11) return 3;
    return 4; // Jan–Mar
}
function quarterRange(fyStart, q) {
    // fyStart = calendar year Apr; q = 1..4
    const startMonthByQ = { 1: 3, 2: 6, 3: 9, 4: 0 }; // 0-indexed month
    const startYear = q === 4 ? fyStart + 1 : fyStart;
    const startMonth = startMonthByQ[q];
    const from = new Date(startYear, startMonth, 1);
    const to = new Date(startYear, startMonth + 3, 0); // last day of q's 3rd month
    return { from, to };
}

// ---------- Preset -> date range resolver ---------- //
// 2026-01 UX: labels use plain month/quarter names ("Jul 2026", "Apr-Jun 2026")
// instead of "This month" / "Q1" jargon so users don't have to translate.

const _SHORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export function resolvePeriod(preset, custom) {
    const today = new Date();
    switch (preset) {
        case 'all':
            return { preset: 'all', from: null, to: null, label: 'All time' };
        case 'this-month': {
            const from = startOfMonth(today), to = endOfMonth(today);
            return {
                preset,
                from: toISODate(from),
                to: toISODate(to),
                label: `${_SHORT_MONTHS[today.getMonth()]} ${today.getFullYear()}`,
            };
        }
        case 'last-month': {
            const base = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            const from = startOfMonth(base), to = endOfMonth(base);
            return {
                preset,
                from: toISODate(from),
                to: toISODate(to),
                label: `${_SHORT_MONTHS[base.getMonth()]} ${base.getFullYear()}`,
            };
        }
        case 'this-quarter': {
            const fy = fyStartYear(today);
            const q = fyQuarter(today);
            const { from, to } = quarterRange(fy, q);
            return {
                preset,
                from: toISODate(from),
                to: toISODate(to),
                label: `${_SHORT_MONTHS[from.getMonth()]}\u2013${_SHORT_MONTHS[to.getMonth()]} ${to.getFullYear()}`,
            };
        }
        case 'last-quarter': {
            const fy = fyStartYear(today);
            const q = fyQuarter(today);
            const lastQ = q === 1 ? 4 : q - 1;
            const lastFy = q === 1 ? fy - 1 : fy;
            const { from, to } = quarterRange(lastFy, lastQ);
            return {
                preset,
                from: toISODate(from),
                to: toISODate(to),
                label: `${_SHORT_MONTHS[from.getMonth()]}\u2013${_SHORT_MONTHS[to.getMonth()]} ${to.getFullYear()}`,
            };
        }
        case 'this-fy': {
            const fy = fyStartYear(today);
            const from = new Date(fy, 3, 1);
            const to = new Date(fy + 1, 2, 31);
            return { preset, from: toISODate(from), to: toISODate(to), label: `Apr ${fy} \u2013 Mar ${fy + 1}` };
        }
        case 'last-fy': {
            const fy = fyStartYear(today) - 1;
            const from = new Date(fy, 3, 1);
            const to = new Date(fy + 1, 2, 31);
            return { preset, from: toISODate(from), to: toISODate(to), label: `Apr ${fy} \u2013 Mar ${fy + 1}` };
        }
        case 'custom': {
            const f = (custom && custom.from) || null;
            const t = (custom && custom.to) || null;
            let label = 'Custom range';
            if (f && t) {
                label = `${fmtHuman(new Date(f))} — ${fmtHuman(new Date(t))}`;
            } else if (f) {
                label = `From ${fmtHuman(new Date(f))}`;
            } else if (t) {
                label = `Until ${fmtHuman(new Date(t))}`;
            }
            return { preset, from: f, to: t, label };
        }
        default:
            return { preset: 'all', from: null, to: null, label: 'All time' };
    }
}

// ---------- Storage / shared hook ---------- //

function loadStored() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && parsed.preset) return parsed;
    } catch { /* noop */ }
    return null;
}

/**
 * useSharedPeriod — mirror of useSharedMonth for the new PeriodPicker.
 * Returns [period, setPeriod] where `period = { preset, from, to, label }`.
 * Defaults to "This FY" so users land on their current-year view.
 */
export function useSharedPeriod(defaultPreset = 'this-fy') {
    const [state, setState] = useState(() => {
        const stored = loadStored();
        if (stored) {
            // Re-resolve to refresh label & (for presets) the range against today.
            if (stored.preset === 'custom') {
                return resolvePeriod('custom', { from: stored.from, to: stored.to });
            }
            return resolvePeriod(stored.preset);
        }
        return resolvePeriod(defaultPreset);
    });

    useEffect(() => {
        const handler = () => {
            const stored = loadStored();
            if (!stored) return;
            if (stored.preset === 'custom') {
                setState(resolvePeriod('custom', { from: stored.from, to: stored.to }));
            } else {
                setState(resolvePeriod(stored.preset));
            }
        };
        window.addEventListener('storage', handler);
        window.addEventListener('billlens:periodchange', handler);
        return () => {
            window.removeEventListener('storage', handler);
            window.removeEventListener('billlens:periodchange', handler);
        };
    }, []);

    const setPeriod = (next) => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({
                preset: next.preset,
                from: next.from,
                to: next.to,
            }));
        } catch { /* noop */ }
        setState(next);
        try { window.dispatchEvent(new Event('billlens:periodchange')); } catch { /* noop */ }
    };

    return [state, setPeriod];
}

/** Convert a `period` into `{ date_from, date_to }` query params. */
export function periodToParams(period) {
    if (!period || period.preset === 'all' || (!period.from && !period.to)) return {};
    const out = {};
    if (period.from) out.date_from = period.from;
    if (period.to) out.date_to = period.to;
    return out;
}

// ---------- The picker component ---------- //

// Dynamically compute human-readable labels for each preset based on today's
// date. Requested UX (2026-01): show actual month names / quarter ranges /
// FY spans instead of relative "This/Last month" wording — much clearer for
// Indian accounting workflows where users think in "Apr–Jun" rather than "Q1".
function _shortMonth(monthIdx) {
    return ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][monthIdx];
}
function _monthNameYear(d) {
    return `${_shortMonth(d.getMonth())} ${d.getFullYear()}`;
}
function _presetLabels() {
    const today = new Date();
    const thisM = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastM = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const fyStart = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
    const fyShort = String((fyStart + 1) % 100).padStart(2, '0');
    const fyLastShort = String(fyStart % 100).padStart(2, '0');
    const fyLastFullEnd = String(fyStart).padStart(2, '0');

    // Indian FY quarter for today (Q1 Apr-Jun, Q2 Jul-Sep, Q3 Oct-Dec, Q4 Jan-Mar)
    const m = today.getMonth();
    const q = m >= 3 && m <= 5 ? 1 : m >= 6 && m <= 8 ? 2 : m >= 9 && m <= 11 ? 3 : 4;
    const QM = { 1: [3, 5, fyStart], 2: [6, 8, fyStart], 3: [9, 11, fyStart], 4: [0, 2, fyStart + 1] };
    const [qs, qe, qy] = QM[q];
    const thisQLabel = `${_shortMonth(qs)}–${_shortMonth(qe)} ${qy}`;

    const lastQ = q === 1 ? 4 : q - 1;
    const lastQFyStart = q === 1 ? fyStart - 1 : fyStart;
    const LQM = { 1: [3, 5, lastQFyStart], 2: [6, 8, lastQFyStart], 3: [9, 11, lastQFyStart], 4: [0, 2, lastQFyStart + 1] };
    const [lqs, lqe, lqy] = LQM[lastQ];
    const lastQLabel = `${_shortMonth(lqs)}–${_shortMonth(lqe)} ${lqy}`;

    return {
        'this-month':   _monthNameYear(thisM),
        'last-month':   _monthNameYear(lastM),
        'this-quarter': thisQLabel,
        'last-quarter': lastQLabel,
        'this-fy':      `FY ${fyStart}-${fyShort} (Apr-Mar)`,
        'last-fy':      `FY ${fyStart - 1}-${fyLastShort} (Apr-Mar)`,
        'all':          'All time',
    };
}

const PRESET_ORDER = ['this-month', 'last-month', 'this-quarter', 'last-quarter', 'this-fy', 'last-fy', 'all'];

export function PeriodPicker({ value, onChange, testid = 'period-picker', className }) {
    const [open, setOpen] = useState(false);
    const [customFrom, setCustomFrom] = useState(value?.preset === 'custom' ? value.from || '' : '');
    const [customTo, setCustomTo] = useState(value?.preset === 'custom' ? value.to || '' : '');
    const anchorRef = useRef(null);

    // Recompute preset labels once per picker mount. They only need to change
    // when the calendar month rolls over, and the picker's parent typically
    // re-mounts across visits.
    const presetLabels = useMemo(() => _presetLabels(), []);

    // Keep local custom inputs in sync when parent value changes externally.
    useEffect(() => {
        if (value?.preset === 'custom') {
            setCustomFrom(value.from || '');
            setCustomTo(value.to || '');
        }
    }, [value?.preset, value?.from, value?.to]);

    const pick = (preset) => {
        onChange(resolvePeriod(preset));
        setOpen(false);
    };

    const applyCustom = () => {
        if (!customFrom && !customTo) return;
        onChange(resolvePeriod('custom', { from: customFrom || null, to: customTo || null }));
        setOpen(false);
    };

    const currentLabel = value?.label || 'All time';

    return (
        <div className={className} ref={anchorRef}>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        data-testid={testid}
                        className="min-w-[200px] justify-between gap-2 font-normal"
                        aria-label="Select period"
                    >
                        <span className="inline-flex items-center gap-2 truncate">
                            <Calendar size={14} className="text-muted-foreground shrink-0" />
                            <span className="truncate">{currentLabel}</span>
                        </span>
                        <ChevronDown size={14} className="text-muted-foreground shrink-0" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-[320px] p-0" data-testid={`${testid}-panel`}>
                    <div className="p-2">
                        <div className="text-[11px] uppercase tracking-wide text-muted-foreground px-2 py-1.5">
                            Presets
                        </div>
                        <div className="grid grid-cols-2 gap-1">
                            {PRESET_ORDER.map((preset) => {
                                const active = value?.preset === preset;
                                return (
                                    <Button
                                        key={preset}
                                        variant={active ? 'default' : 'ghost'}
                                        size="sm"
                                        onClick={() => pick(preset)}
                                        className="justify-start"
                                        data-testid={`${testid}-preset-${preset}`}
                                    >
                                        {presetLabels[preset]}
                                    </Button>
                                );
                            })}
                        </div>
                    </div>
                    <Separator />
                    <div className="p-3 space-y-2.5">
                        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                            Custom range
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                                <Label htmlFor={`${testid}-from`} className="text-xs">From</Label>
                                <Input
                                    id={`${testid}-from`}
                                    type="date"
                                    value={customFrom}
                                    onChange={(e) => setCustomFrom(e.target.value)}
                                    data-testid={`${testid}-from`}
                                    className="h-9"
                                />
                            </div>
                            <div className="space-y-1">
                                <Label htmlFor={`${testid}-to`} className="text-xs">To</Label>
                                <Input
                                    id={`${testid}-to`}
                                    type="date"
                                    value={customTo}
                                    onChange={(e) => setCustomTo(e.target.value)}
                                    data-testid={`${testid}-to`}
                                    className="h-9"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end pt-1">
                            <Button
                                size="sm"
                                onClick={applyCustom}
                                disabled={!customFrom && !customTo}
                                data-testid={`${testid}-apply-custom`}
                            >
                                Apply range
                            </Button>
                        </div>
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
}
